import { DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select } from "antd";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { RequestConnectionPropertyType } from "types/enums";
import { DrawerId } from "types/models";

import FormFieldData = App.Data.FormFieldData;

const { Option } = Select;
interface UrlParametersFormProps {
    name: number;
    parentName: number;
    leadFields: Array<FormFieldData | string>;
    handleRemove: () => void;
}

export const UrlParametersForm: FC<UrlParametersFormProps> = ({ name, parentName, leadFields, handleRemove }) => {
    const { t } = usePaldockTranslation("ui.connection-creator-editor.url-parameters");
    const reqSetting = DrawerId.MethodPropertyUrlSetting;
    const generalFormUtils = useGeneralForm();
    const { control } = generalFormUtils;

    const propertyUrlPath = `methods.${parentName}.requestConfig.requestConnectionPropertiesUrl.${name}` as const;

    const type = useWatch({
        control,
        name: `${propertyUrlPath}.type`,
        defaultValue: RequestConnectionPropertyType.Constant,
    });

    return (
        <Row gutter={16} align="middle">
            <Col span={6}>
                <FormItem control={control} name={`${propertyUrlPath}.type`} label={t("fields.type.label")}>
                    <Select placeholder={t("fields.type.placeholder")}>
                        {Object.values(RequestConnectionPropertyType).map((property) => (
                            <Select.Option value={property} key={property}>
                                {property}
                            </Select.Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>

            <Col span={6}>
                <FormItem control={control} name={`${propertyUrlPath}.use_as_accessor`} label={t("fields.key.label")}>
                    <Input placeholder={t("fields.key.placeholder")} />
                </FormItem>
            </Col>

            <Col span={6}>
                {type === RequestConnectionPropertyType.Object ? (
                    <FormItem control={control} name={`${propertyUrlPath}.accessor`} label={t("fields.accessor.label")}>
                        <Select placeholder={t("fields.accessor.placeholder")}>
                            {leadFields?.map((field) =>
                                isFormFieldData(field) ? (
                                    <Option key={field.id} value={field.system_name}>
                                        {field.system_name}
                                    </Option>
                                ) : (
                                    <Option key={field} value={field}>
                                        {field}
                                    </Option>
                                )
                            )}
                        </Select>
                    </FormItem>
                ) : (
                    <FormItem control={control} name={`${propertyUrlPath}.value`} label={t("fields.value.label")}>
                        <Input placeholder={t("fields.value.placeholder")} />
                    </FormItem>
                )}
            </Col>

            <Col style={{ textAlign: "left" }}>
                <Button danger onClick={handleRemove} icon={<DeleteOutlined />} />
            </Col>

            <Col>
                <DrawerLink
                    drawerId={reqSetting}
                    search={{
                        [`${reqSetting}-id`]: name.toString(),
                        [`${reqSetting}-parentName`]: parentName.toString(),
                        [`${reqSetting}-propertyType`]: type,
                    }}
                    action="show"
                >
                    <Button icon={<SettingOutlined />} />
                </DrawerLink>
            </Col>
        </Row>
    );
};

const isFormFieldData = (field: FormFieldData | string): field is FormFieldData => {
    return typeof field === "object" && "id" in field && "system_name" in field;
};
